const CURRENCY = "GBP"
const trackedEventKeys = new Set()
const bookingState = {
  booking: {},
  extras: [],
}

function getDataLayer() {
  window.dataLayer = window.dataLayer || []

  return window.dataLayer
}

function cleanObject(object) {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined && value !== null && value !== "")
  )
}

function money(value) {
  const numericValue = parseFloat(String(value ?? "").replace(/,/g, ""))

  return Number.isNaN(numericValue) ? 0 : Math.round(numericValue * 100) / 100
}

function integer(value, fallback = 0) {
  const numericValue = parseInt(value, 10)

  return Number.isNaN(numericValue) ? fallback : numericValue
}

function bookingRoot() {
  return document.querySelector("#holiday-park-booking-page")
}

function bookingPaymentType() {
  return bookingRoot()?.querySelector("#paying_deposit")?.value === "true" ? "deposit" : "full"
}

function bookingCheckoutValue() {
  return money(
    bookingState.booking?.checkout_total_price
    ?? bookingRoot()?.dataset?.checkoutTotalPrice
    ?? bookingState.booking?.total_price
    ?? bookingRoot()?.dataset?.totalPrice
    ?? 0
  )
}

function bookingPaymentValue() {
  if (bookingPaymentType() === "deposit") {
    return money(
      bookingState.booking?.deposit_checkout_total_price
      ?? bookingRoot()?.dataset?.depositCheckoutTotalPrice
      ?? bookingState.booking?.deposit_amount
      ?? bookingRoot()?.dataset?.depositAmount
      ?? bookingCheckoutValue()
    )
  }

  return bookingCheckoutValue()
}

function bookingSelectedExtras() {
  return Array.isArray(bookingState.extras)
    ? bookingState.extras.filter((extra) => integer(extra?.quantity, 0) > 0)
    : []
}

function bookingExtrasItems() {
  return bookingSelectedExtras().map((extra) => buildExtraItem(extra))
}

function bookingSecurityDepositDueNow() {
  return Boolean(
    bookingState.booking?.security_deposit_due_now
    ?? (bookingRoot()?.dataset?.securityDepositDueNow === "true")
  )
}

function bookingSecurityDepositValue() {
  if (!bookingSecurityDepositDueNow()) {
    return 0
  }

  return money(
    bookingState.booking?.security_deposit_amount
    ?? bookingRoot()?.dataset?.securityDepositAmount
    ?? 0
  )
}

function bookingSecurityDepositItem() {
  if (!bookingSecurityDepositDueNow() || bookingSecurityDepositValue() <= 0) {
    return null
  }

  const root = bookingRoot()

  return buildAccommodationItem({
    itemId: `${root?.dataset?.grade_code ?? bookingState.booking?.grade_code ?? root?.dataset?.booking_id ?? "holiday-park-booking"}-security-deposit`,
    itemName: `${root?.dataset?.accommodationTitle ?? "Holiday home"} Security Deposit`,
    itemCategory: root?.dataset?.accommodationCategory ?? "Holiday Park Booking",
    itemVariant: "security_deposit",
    price: bookingSecurityDepositValue(),
    quantity: 1,
  })
}

function bookingBaseItem() {
  const root = bookingRoot()
  const extrasValue = bookingExtrasItems().reduce((total, item) => total + money(item.price), 0)
  const stayValue = money(bookingState.booking?.total_price ?? root?.dataset?.totalPrice ?? 0)

  return buildAccommodationItem({
    itemId: root?.dataset?.grade_code ?? bookingState.booking?.grade_code ?? root?.dataset?.booking_id ?? "holiday-park-booking",
    itemName: root?.dataset?.accommodationTitle ?? "Holiday home",
    itemCategory: root?.dataset?.accommodationCategory ?? "Holiday Park Booking",
    itemVariant: bookingPaymentType(),
    price: Math.max(stayValue - extrasValue, 0),
    quantity: 1,
  })
}

function bookingItems() {
  return [bookingBaseItem(), ...bookingExtrasItems(), bookingSecurityDepositItem()]
    .filter((item) => Boolean(item?.item_id || item?.item_name))
}

function normalizedItemsWithList(items, listName) {
  return items.map((item, index) => cleanObject({
    ...item,
    index: index + 1,
    item_list_name: listName,
  }))
}

function pushEcommerceEvent(eventName, ecommerce, { dedupeKey } = {}) {
  if (dedupeKey && trackedEventKeys.has(dedupeKey)) {
    return false
  }

  const dataLayer = getDataLayer()

  dataLayer.push({ ecommerce: null })
  dataLayer.push({
    event: eventName,
    ecommerce,
  })

  if (dedupeKey) {
    trackedEventKeys.add(dedupeKey)
  }

  return true
}

function buildAccommodationItem(attributes = {}) {
  return cleanObject({
    item_id: attributes.item_id ?? attributes.itemId,
    item_name: attributes.item_name ?? attributes.itemName ?? "Holiday home",
    item_category: attributes.item_category ?? attributes.itemCategory,
    item_variant: attributes.item_variant ?? attributes.itemVariant,
    price: money(attributes.price),
    quantity: Math.max(1, integer(attributes.quantity, 1)),
  })
}

function buildExtraItem(extra = {}, attributes = {}) {
  return cleanObject({
    item_id: attributes.item_id ?? attributes.itemId ?? extra.code,
    item_name: attributes.item_name ?? attributes.itemName ?? extra.name ?? extra.description ?? "Extra",
    item_category: attributes.item_category ?? attributes.itemCategory ?? "Optional Extra",
    item_variant: attributes.item_variant ?? attributes.itemVariant ?? extra.pricing_type ?? extra.pricing_method_type,
    price: money(attributes.price ?? extra.price ?? 0),
    quantity: Math.max(1, integer(attributes.quantity ?? extra.quantity, 1)),
  })
}

function updateBookingAnalyticsState(booking = {}) {
  bookingState.booking = booking ?? {}
}

function updateBookingAnalyticsExtras(extras = []) {
  bookingState.extras = Array.isArray(extras) ? extras : []
}

function trackBookingPageView() {
  const root = bookingRoot()

  if (!root) {
    return
  }

  const items = bookingItems()
  const value = bookingCheckoutValue() || items.reduce((total, item) => total + money(item.price), 0)
  const eventScope = root.dataset.booking_id ?? root.dataset.grade_code ?? "holiday-park-booking"

  pushEcommerceEvent("view_item", {
    currency: CURRENCY,
    value,
    items,
  }, {
    dedupeKey: `booking:view_item:${eventScope}`,
  })

  pushEcommerceEvent("add_to_cart", {
    currency: CURRENCY,
    value,
    items,
  }, {
    dedupeKey: `booking:add_to_cart:${eventScope}`,
  })
}

function trackBeginCheckout() {
  const root = bookingRoot()

  if (!root) {
    return
  }

  pushEcommerceEvent("begin_checkout", {
    currency: CURRENCY,
    value: bookingCheckoutValue(),
    items: bookingItems(),
  }, {
    dedupeKey: `booking:begin_checkout:${root.dataset.booking_id ?? "default"}`,
  })
}

function trackViewCart() {
  const root = bookingRoot()

  if (!root) {
    return
  }

  pushEcommerceEvent("view_cart", {
    currency: CURRENCY,
    value: bookingCheckoutValue(),
    items: bookingItems(),
  }, {
    dedupeKey: `booking:view_cart:${root.dataset.booking_id ?? "default"}`,
  })
}

function trackAddPaymentInfo() {
  const root = bookingRoot()

  if (!root) {
    return
  }

  pushEcommerceEvent("add_payment_info", {
    currency: CURRENCY,
    value: bookingPaymentValue(),
    payment_type: bookingPaymentType(),
    items: bookingItems(),
  })
}

function trackExtrasQuantityChange({ beforeExtra = null, afterExtra = null } = {}) {
  const previousQuantity = integer(beforeExtra?.quantity, 0)
  const nextQuantity = integer(afterExtra?.quantity, 0)

  if (previousQuantity === nextQuantity) {
    return
  }

  const eventName = nextQuantity > previousQuantity ? "add_to_cart" : "remove_from_cart"
  const quantityDifference = Math.max(1, Math.abs(nextQuantity - previousQuantity))
  const previousValue = money(beforeExtra?.price ?? 0)
  const nextValue = money(afterExtra?.price ?? 0)
  const valueDifference = Math.max(0, Math.abs(nextValue - previousValue))
  const sourceExtra = nextQuantity > previousQuantity ? afterExtra : beforeExtra

  pushEcommerceEvent(eventName, {
    currency: CURRENCY,
    value: valueDifference || money(sourceExtra?.price ?? 0),
    items: [
      buildExtraItem(sourceExtra, {
        price: valueDifference || (sourceExtra?.price ?? 0),
        quantity: quantityDifference,
      }),
    ],
  })
}

function trackViewItemList({ listName, items, dedupeKey }) {
  if (!Array.isArray(items) || items.length === 0) {
    return
  }

  pushEcommerceEvent("view_item_list", {
    item_list_name: listName,
    items: normalizedItemsWithList(items, listName),
  }, {
    dedupeKey,
  })
}

function trackSelectItem({ listName, item, index = 1 }) {
  if (!item) {
    return
  }

  pushEcommerceEvent("select_item", {
    item_list_name: listName,
    items: [
      cleanObject({
        ...item,
        index,
        item_list_name: listName,
      }),
    ],
  })
}

export {
  buildAccommodationItem,
  buildExtraItem,
  trackAddPaymentInfo,
  trackBeginCheckout,
  trackBookingPageView,
  trackExtrasQuantityChange,
  trackSelectItem,
  trackViewCart,
  trackViewItemList,
  updateBookingAnalyticsExtras,
  updateBookingAnalyticsState,
}
